// Tier 2 — fetch the deployed build id from a static /version.json and compare
// it to the loaded bundle's id. Pure-ish (only side effect is the fetch);
// trivially unit-testable by mocking global fetch.
//
// Failure policy (SPEC §2 "no backend"; design §5.3/§10.6): a missing file,
// 404, offline, malformed JSON, or aborted request ALL map to
// outcome:'unknown'. The coordinator treats 'unknown' exactly like 'same' —
// it is NEVER surfaced to the user. Tier 2 is a best-effort convenience layer.
//
// ROOT-DOMAIN ASSUMPTION (design §13 item 3): vite.config uses `base: './'`
// (relative bundle assets), but version.json is fetched at the ABSOLUTE path
// `/version.json`. This assumes a root-domain deploy — true for
// letterflick.com per SPEC §11. A non-root deploy would 404 here and degrade
// to a silent 'unknown' no-op (Tier 1 is unaffected; it does no network).

export interface VersionCheckResult {
  readonly outcome: 'same' | 'newer' | 'unknown'
  readonly servedBuildId?: string
}

export async function fetchServedBuildId(
  loadedBuildId: string,
  signal?: AbortSignal,
): Promise<VersionCheckResult> {
  try {
    // Two-layer cache-busting: `cache: 'no-store'` defeats the browser HTTP
    // cache; the `?t=` query param defeats intermediary/CDN caches that ignore
    // no-store on GET. The query param is the load-bearing one for the CDN.
    const url = `/version.json?t=${String(Date.now())}`
    const res = await fetch(url, {
      cache: 'no-store',
      headers: { accept: 'application/json' },
      ...(signal !== undefined ? { signal } : {}),
    })
    if (!res.ok) return { outcome: 'unknown' }
    const data: unknown = await res.json()
    if (
      data === null ||
      typeof data !== 'object' ||
      typeof (data as Record<string, unknown>)['buildId'] !== 'string'
    ) {
      return { outcome: 'unknown' }
    }
    const served = (data as { buildId: string }).buildId
    return served === loadedBuildId
      ? { outcome: 'same' }
      : { outcome: 'newer', servedBuildId: served }
  } catch {
    // Offline, 404 that throws, JSON parse error, abort — all silent no-ops.
    return { outcome: 'unknown' }
  }
}
